"use client";

import { FAQAccordion } from "@/components/FaqAccordion";

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  /** Title of the FAQ section */
  title?: string;
  /** Subtitle/description below the title */
  subtitle?: string;
  /** FAQ items to display */
  items: FAQItem[];
  /** Background color class, defaults to "bg-white" */
  bgColor?: string;
}

/**
 * FAQSection - A reusable FAQ section component with consistent styling
 *
 * Used by:
 * - Pricing page (PricingPageClient)
 * - Templates index (templates-page-client)
 * - Template detail pages (TemplateSEOContent)
 *
 * The homepage renders `FAQAccordion` directly instead, inside its own card shell
 * (every sibling SEO block on that page uses the same shell). All four call sites
 * therefore go through the same shadcn accordion.
 *
 * Features:
 * - Consistent max-w-5xl container width
 * - Optional title and subtitle
 * - Uses FAQAccordion for the accordion behavior
 * - Configurable background color
 */
export function FAQSection({
  title = "Frequently Asked Questions",
  subtitle,
  items,
  bgColor = "bg-white",
}: FAQSectionProps) {
  if (items.length === 0) return null;

  return (
    <section className={`py-16 ${bgColor} dark:bg-transparent relative`}>
      {/* 顶部装饰性分隔线 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-gray-600 to-transparent" />

      <div className="container mx-auto px-4 max-w-5xl">
        <h2
          className={`text-3xl font-bold text-center text-gray-900 dark:text-white ${subtitle ? "mb-4" : "mb-12"}`}
        >
          {title}
        </h2>
        {subtitle && (
          <p className="text-center text-gray-600 dark:text-gray-300 mb-12">
            {subtitle}
          </p>
        )}
        <div className="bg-gray-50 dark:bg-gray-800/80 rounded-xl border border-slate-200 dark:border-gray-600 p-6 md:p-8 shadow-lg">
          <FAQAccordion items={items} />
        </div>
      </div>
    </section>
  );
}
